<script lang="ts">
  import {
    SPONSORBLOCK_ATTRIBUTION,
    activeSponsorSegment,
    sponsorMarkers,
    sponsorSegmentsFor,
  } from '../player/sponsorblock';
  import type { RoomState } from '../room/state.svelte';
  import { formatDuration, formatSignedMs, playbackLabel } from './format';

  const { room }: { room: RoomState } = $props();

  const player = $derived(room.player);

  let videoElement = $state<HTMLVideoElement | null>(null);
  let joinButton = $state<HTMLButtonElement | null>(null);
  /** True while the user drags the scrubber, so corrections do not fight them. */
  let scrubbing = $state(false);
  let scrubMs = $state(0);

  const playback = $derived(room.playback);
  const permissions = $derived(room.permissions);
  const canControl = $derived(permissions?.canControlPlayback === true && room.live);
  const hasMedia = $derived(
    playback.state === 'playing' || playback.state === 'paused' || playback.state === 'buffering',
  );
  const durationMs = $derived(hasMedia ? player.durationMs : 0);
  const displayPositionMs = $derived(scrubbing ? scrubMs : player.positionMs);
  const waitingOn = $derived(
    playback.state === 'buffering'
      ? playback.waiting_on.map(
          (identity) =>
            room.participants.find((participant) => participant.identity === identity)?.username ??
            'someone who just left',
        )
      : [],
  );
  /**
   * After a buffering barrier times out the room simply stays `paused`; the
   * only remaining trace of why is the presence of the participants still
   * stalled. Naming them keeps the outcome visible instead of leaving an
   * unexplained pause.
   */
  const stalledWhilePaused = $derived(
    playback.state === 'paused'
      ? room.participants
          .filter((participant) => participant.presence === 'buffering')
          .map((participant) => participant.username)
      : [],
  );
  const currentEntry = $derived(playback.state === 'idle' ? null : playback.entry);
  const sponsorSegments = $derived(sponsorSegmentsFor(playback));
  const sponsorMarks = $derived(sponsorMarkers(sponsorSegments, durationMs));
  /**
   * Membership is tested against the room's authoritative position, not this
   * element's, so every client offers the skip over the same interval — and
   * the server re-validates it before moving anyone.
   */
  const activeSegment = $derived(activeSponsorSegment(sponsorSegments, player.roomPositionMs));

  $effect(() => {
    player.setVideoElement(videoElement);
    return () => player.setVideoElement(null);
  });

  // Focus the join affordance when autoplay is refused: it is the only way to
  // start playback, so it must be reachable without hunting for it.
  $effect(() => {
    if (player.autoplayBlocked) joinButton?.focus();
  });

  function togglePlay(): void {
    void room.run({ type: playback.state === 'playing' ? 'pause' : 'play' });
  }

  function commitSeek(event: Event): void {
    scrubbing = false;
    const target = event.currentTarget;
    if (!(target instanceof HTMLInputElement)) return;
    void room.run({ type: 'seek', position_ms: Math.max(0, Math.round(Number(target.value))) });
  }

  function startScrub(event: Event): void {
    const target = event.currentTarget;
    if (!(target instanceof HTMLInputElement)) return;
    scrubbing = true;
    scrubMs = Number(target.value);
  }

  /**
   * Requests the shared skip for the segment the room is currently inside.
   * The command carries the entry and the server's segment key — never a
   * client-computed end timestamp — and the room re-validates both, so a
   * second click or a race with another viewer is rejected as already-applied
   * instead of jumping again. That rejection is deliberately not surfaced as
   * an error (see `rejectionNotice` in `room/state.svelte.ts`).
   */
  function skipSponsor(): void {
    const segment = activeSegment;
    if (segment === null || currentEntry === null) return;
    void room.run({ type: 'skip-sponsor-segment', entry: currentEntry, segment: segment.key });
  }
</script>

<section class="player" aria-label="Player">
  <div class="stage" class:empty={!hasMedia}>
    <!-- Captions are attached as `<track>` children by the media adapter for
         the active generation, so the static caption check cannot see them. -->
    <!-- svelte-ignore a11y_media_has_caption -->
    <video
      bind:this={videoElement}
      class:hidden={!hasMedia}
      preload="auto"
      playsinline
      aria-label="Room video"
    ></video>

    {#if !hasMedia}
      <p class="stage-message">
        {#if playback.state === 'preparing'}
          Preparing the video on the server…
        {:else if playback.state === 'failed'}
          <strong>This video failed:</strong> {playback.message}
          <span class="reason">({playback.reason})</span>
        {:else}
          Nothing is playing. Add a video URL below to start.
        {/if}
      </p>
    {/if}

    {#if player.unsupported !== null}
      <p class="overlay" role="alert">{player.unsupported}</p>
    {:else if player.localError !== null}
      <p class="overlay" role="alert">{player.localError}</p>
    {:else if player.autoplayBlocked}
      <div class="overlay join">
        <p>Your browser blocked playback until you interact with the page.</p>
        <button bind:this={joinButton} type="button" onclick={() => player.joinPlayback()}>
          Join playback
        </button>
        <p class="hint">This only starts your own player at the room's current position.</p>
      </div>
    {:else if playback.state === 'buffering'}
      <p class="overlay soft" role="status">
        Waiting for {waitingOn.length === 0 ? 'a viewer' : waitingOn.join(', ')} to buffer.
      </p>
    {:else if stalledWhilePaused.length > 0}
      <p class="overlay soft" role="status">
        Paused — {stalledWhilePaused.join(', ')}
        {stalledWhilePaused.length === 1 ? 'is' : 'are'} still buffering. Press play when ready.
      </p>
    {/if}
  </div>

  <div class="controls">
    <button
      type="button"
      class="primary"
      onclick={togglePlay}
      disabled={!canControl || !hasMedia}
      title={canControl ? '' : 'The host limited playback control to controllers.'}
    >
      {playback.state === 'playing' ? 'Pause' : 'Play'}
    </button>

    <span class="time" aria-hidden="true">{formatDuration(displayPositionMs)}</span>
    <label class="scrub">
      <span class="visually-hidden">Seek position</span>
      <input
        type="range"
        min="0"
        max={Math.max(durationMs, 1)}
        step="1000"
        value={displayPositionMs}
        disabled={!canControl || !hasMedia}
        oninput={startScrub}
        onchange={commitSeek}
        aria-valuetext={formatDuration(displayPositionMs)}
      />
      {#if sponsorMarks.length > 0}
        <!-- Inert overlay: the marks are an indication, not a control, so
             they never intercept a drag on the slider underneath. The
             positions are the only inline styles in the app because they are
             data, not design. -->
        <span class="sponsor-marks" aria-hidden="true">
          {#each sponsorMarks as mark (mark.key)}
            <span
              class="sponsor-mark"
              class:current={activeSegment?.key === mark.key}
              style:left="{mark.startPercent}%"
              style:width="{mark.widthPercent}%"
            ></span>
          {/each}
        </span>
      {/if}
    </label>
    <span class="time" aria-hidden="true">{formatDuration(durationMs)}</span>

    <button type="button" onclick={() => player.setMuted(!player.muted)}>
      {player.muted ? 'Unmute' : 'Mute'}
    </button>
    <label class="volume">
      <span class="visually-hidden">Volume</span>
      <input
        type="range"
        min="0"
        max="1"
        step="0.05"
        value={player.volume}
        oninput={(event) => player.setVolume(Number(event.currentTarget.value))}
      />
    </label>

    {#if player.subtitles.length > 0}
      <label class="captions">
        <span class="visually-hidden">Captions</span>
        <select
          value={player.activeSubtitle === null ? 'off' : String(player.activeSubtitle)}
          onchange={(event) => {
            const value = event.currentTarget.value;
            player.selectSubtitle(value === 'off' ? null : Number(value));
          }}
        >
          <option value="off">Captions off</option>
          {#each player.subtitles as track, index (track.url)}
            <option value={String(index)}>
              {track.label}{track.auto_generated ? ' (auto)' : ''}
            </option>
          {/each}
        </select>
      </label>
    {/if}

    <button type="button" onclick={() => void player.toggleFullscreen()}>Fullscreen</button>
  </div>

  {#if hasMedia && sponsorSegments.length > 0}
    <div class="sponsor" class:current={activeSegment !== null}>
      <p class="sponsor-state" role="status">
        {#if activeSegment !== null}
          <strong>Sponsor segment</strong> — runs to {formatDuration(activeSegment.end_ms)}.
        {:else}
          {sponsorSegments.length}
          {sponsorSegments.length === 1 ? 'sponsor segment' : 'sponsor segments'} marked on the
          seek bar. Nothing is skipped automatically.
        {/if}
      </p>
      {#if activeSegment !== null}
        <button
          type="button"
          onclick={skipSponsor}
          disabled={!canControl || room.isBusy('skip-sponsor-segment')}
          title={canControl
            ? 'Seeks everyone to the end of this segment.'
            : 'The host limited playback control to controllers.'}
        >
          Skip for everyone
        </button>
      {/if}
      <p class="attribution">
        Segment data:
        <a href={SPONSORBLOCK_ATTRIBUTION.url} target="_blank" rel="noreferrer noopener">
          {SPONSORBLOCK_ATTRIBUTION.name}
        </a>
      </p>
    </div>
  {/if}

  <p class="state-line" role="status">
    <strong>{playbackLabel(playback)}</strong>
    {#if room.clock === null}
      · estimating room clock…
    {:else if player.driftMs !== null}
      · your player is {formatSignedMs(-player.driftMs)} from the room
      {#if player.appliedRate !== 1}· correcting at {player.appliedRate.toFixed(3)}×{/if}
    {/if}
  </p>

  <details class="sync">
    <summary>Synchronization detail</summary>
    <dl>
      <dt>Clock offset</dt>
      <dd>{room.clock === null ? 'unknown' : formatSignedMs(room.clock.offsetMs)}</dd>
      <dt>Round trip</dt>
      <dd>
        {room.clock?.rttMs == null ? 'unknown' : `${Math.round(room.clock.rttMs)} ms`}
        ({room.clock === null ? 'no samples' : `${room.clock.samples} samples, ${room.clock.quality}`})
      </dd>
      <dt>Drift</dt>
      <dd>{player.driftMs === null ? 'not measurable' : formatSignedMs(player.driftMs)}</dd>
      <dt>Playback rate</dt>
      <dd>{player.appliedRate.toFixed(3)}×</dd>
      <dt>Corrective seeks</dt>
      <dd>{player.correctiveSeeks}</dd>
      <dt>Seek lead</dt>
      <dd>{Math.round(player.seekLeadMs)} ms</dd>
      <dt>My reported state</dt>
      <dd>{player.reportState}</dd>
      <dt>Buffered to</dt>
      <dd>{formatDuration(player.bufferedMs)}</dd>
    </dl>
  </details>
</section>

<style>
  .player {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    min-width: 0;
  }

  .stage {
    position: relative;
    background: #000;
    border: 1px solid var(--wa-border);
    border-radius: 0.5rem;
    overflow: hidden;
    /* A flexible height keeps the queue and controls on screen instead of
       pushing them below a fixed-height player. */
    aspect-ratio: 16 / 9;
    max-height: 62vh;
    display: grid;
    place-items: center;
  }

  .stage.empty {
    background: var(--wa-surface);
    border-style: dashed;
  }

  video {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: contain;
  }

  video.hidden {
    display: none;
  }

  .stage-message {
    color: var(--wa-text-dim);
    margin: 0;
    padding: 1.5rem;
    text-align: center;
  }

  .overlay {
    position: absolute;
    inset: auto 0.75rem 0.75rem;
    margin: 0;
    background: var(--wa-danger-surface);
    border: 1px solid #6c2f36;
    color: var(--wa-danger);
    border-radius: 0.4rem;
    padding: 0.6rem 0.75rem;
  }

  .overlay.soft {
    background: rgb(23 28 35 / 92%);
    border-color: var(--wa-border);
    color: var(--wa-text);
  }

  .overlay.join {
    inset: auto;
    text-align: center;
    background: rgb(14 17 22 / 94%);
    border-color: var(--wa-accent);
    color: var(--wa-text);
    padding: 1.25rem 1.5rem;
  }

  .overlay.join p {
    margin: 0 0 0.6rem;
  }

  .overlay.join .hint {
    margin: 0.6rem 0 0;
    font-size: 0.85rem;
    color: var(--wa-text-dim);
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .scrub {
    flex: 1 1 12rem;
    min-width: 8rem;
    display: flex;
    position: relative;
  }

  /* The marks sit over the slider's track, which the native range input
     centres vertically; `pointer-events: none` keeps the slider draggable
     across them. */
  .sponsor-marks {
    position: absolute;
    inset: 50% 0 auto;
    height: 0.45rem;
    transform: translateY(-50%);
    pointer-events: none;
  }

  .sponsor-mark {
    position: absolute;
    top: 0;
    bottom: 0;
    background: var(--wa-warning);
    opacity: 0.55;
    border-radius: 1px;
  }

  /* Colour alone never carries meaning: the current segment also gets the
     labelled row and button below the bar. */
  .sponsor-mark.current {
    opacity: 1;
    outline: 1px solid var(--wa-text);
  }

  .scrub input {
    width: 100%;
  }

  .volume input {
    width: 6rem;
  }

  .time {
    font-variant-numeric: tabular-nums;
    color: var(--wa-text-dim);
    font-size: 0.9rem;
  }

  .sponsor {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.6rem;
    border: 1px solid var(--wa-border);
    border-radius: 0.4rem;
    padding: 0.4rem 0.6rem;
    font-size: 0.9rem;
    color: var(--wa-text-dim);
  }

  .sponsor.current {
    border-color: #6c5a2f;
    background: var(--wa-warning-surface);
    color: var(--wa-warning);
  }

  .sponsor-state {
    margin: 0;
    flex: 1 1 14rem;
    min-width: 0;
  }

  .sponsor .attribution {
    margin: 0;
    font-size: 0.8rem;
    color: var(--wa-text-dim);
  }

  .state-line {
    margin: 0;
    color: var(--wa-text-dim);
    font-size: 0.9rem;
  }

  .reason {
    color: #d9a7ab;
    font-size: 0.85rem;
  }

  .sync {
    font-size: 0.85rem;
    color: var(--wa-text-dim);
  }

  .sync dl {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.15rem 0.75rem;
    margin: 0.5rem 0 0;
  }

  .sync dt {
    color: var(--wa-text-dim);
  }

  .sync dd {
    margin: 0;
    color: var(--wa-text);
    font-variant-numeric: tabular-nums;
  }
</style>
